'use client';

import React from 'react';
import styled from 'styled-components';

import { media } from '../../mixins/media';
import type { Icon } from '../../types/icon';
import type { DrawerHeaderBaseProps } from '@rulebooks/evidend-titan/dist/types/components/Drawer/types';
import {
  Avatar,
  Button,
  CloseIcon,
  Heading,
  IconButton,
  useCloseDrawer,
  useGreaterThan,
  withTitan,
} from '@rulebooks/evidend-titan';
import { Trans } from '@lingui/macro';
import { useRouter } from 'next/router';
import { useAccount } from '../../hooks/useAccount';

export interface DrawerHeaderAvatarProps extends DrawerHeaderBaseProps {
  /** Avatar image source. */
  avatar?: string;
  /** Avatar icon. */
  avatarIcon?: React.ReactElement<Icon>;
  /** Avatar initials. */
  initials?: string;
}

const StyledDrawerHeaderAvatar = styled.div`
  position: relative;
`;

const StyledDrawerHeaderBackground = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 72px;
  background: ${({ theme }) => theme.components.drawer.backgroundColorSecondary};

  ${media.greaterThan('s')`
    height: 136px;
  `}
`;

const StyledDrawerHeaderAvatarContainer = styled.div`
  padding: ${({ theme }) => theme.components.drawer.header.avatarPaddingMobile};

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${media.greaterThan('s')`
    padding: ${({ theme }) => theme.components.drawer.header.avatarPadding};
  `}
`;

const StyledDrawerHeading = styled(Heading)`
  margin-bottom: 0;
  text-align: center;
  ${({ theme }) => media.lessThan('s')`
    margin-left: ${theme.components.drawer.header.headingMobileMarginLeft};
    margin-top: 2px;
    font-size: ${theme.components.headings.h5.mobile.fontSize};
  `}
`;

const StyledDrawerMetadata = styled.div`
  text-align: center;
`;

const DrawerHeaderAvatar = withTitan<DrawerHeaderAvatarProps, HTMLDivElement>(
  (
    {
      label,
      avatar,
      initials,
      avatarIcon,
      metadata,
      hasCloseButton = true,
      closeIcon,
      labelClose = 'Sluiten',
      testId = 'drawerHeaderAvatar',
    },
    ref,
  ) => {
    const close = useCloseDrawer();
    const isLarge = useGreaterThan('s');
    const router = useRouter();
    const account = useAccount();

    return (
      <>
        <StyledDrawerHeaderAvatar ref={ref} data-test-id={testId}>
          <StyledDrawerHeaderBackground aria-hidden />
          {hasCloseButton && (
            <StyledDrawerCloseButton
              data-test-id="closeDrawer"
              icon={closeIcon || <CloseIcon />}
              label={labelClose}
              size={20}
              type="button"
              variant="secondary"
              onClick={close}
            />
          )}
          <StyledDrawerHeaderAvatarContainer>
            <Avatar size={isLarge ? 96 : 80} spaceAfter={4} src={avatar} testId={`${testId}Avatar`}>
              {initials || avatarIcon}
            </Avatar>
            {label && <StyledDrawerHeading heading="h3">{label}</StyledDrawerHeading>}
            {metadata && <StyledDrawerMetadata>{metadata}</StyledDrawerMetadata>}

            {!account?.hasLicenseAgreement && (
              <Button
                variant="primary"
                size="S"
                style={{ marginBlockStart: '1rem' }}
                onClick={() => router.push('/account-upgrade')}
              >
                <Trans>Upgrade account</Trans>
              </Button>
            )}
          </StyledDrawerHeaderAvatarContainer>
        </StyledDrawerHeaderAvatar>
      </>
    );
  },
  { name: 'DrawerHeaderAvatar' },
);

const StyledDrawerCloseButton = styled(IconButton)`
  position: absolute;
  right: 20px;
  top: 20px;
`;

export { DrawerHeaderAvatar };
